import React from 'react';
import Field from '../../Field';
import { READING_WORDING } from '../../../domain/reading';
import { Radio, Check } from '../../FormControls';
import { durationError, DURATION_MAX_MINUTES } from '../../../domain/eventSeries';
import s from './eventDrawer.module.css';
import { DateInput, TimeInput } from '../../RegionInputs';

// Who, what and when. Which of those apply depends on the kind of event being
// added — a non-teaching event has nobody attending, and a quick-add takes one
// student and no visible Duration field.
//
// A quick-add still carries a duration: the attending student's own default
// (`students/{id}.defaultDurationMin`, #241), seeded by `useEventForm` when the
// attendee is picked. There is no studio-level duration setting.

function Attendees({ form, set, students, studentsRead = true, shape }) {
  if (shape.attendees === 'none') return null;

  if (shape.attendees === 'single') {
    // "Open lesson slot" is a real thing to schedule: a weekly slot held before
    // anyone is assigned to it. It is simply an event with no attendees.
    return (
      <>
        <Radio
          name="etype" label="Lesson with a student"
          checked={!form.openSlot}
          onChange={() => set('openSlot', false)}
        />
        <Radio
          name="etype" label="Open lesson slot" help="No assigned student"
          checked={form.openSlot}
          onChange={() => { set('openSlot', true); set('studentIds', []); }}
        />
        {!form.openSlot ? (
          <div className={s.spaced}>
            <Field label="Student">
              <select
                className={s.input}
                value={form.studentIds[0] || ''}
                onChange={(e) => set('studentIds', e.target.value ? [e.target.value] : [])}
              >
                <option value="">Select…</option>
                {students.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
              </select>
            </Field>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Field label="Attendees">
        <select
          multiple
          className={s.attendees}
          value={form.studentIds}
          onChange={(e) => set('studentIds', Array.from(e.target.selectedOptions, (o) => o.value))}
        >
          {students.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      </Field>
      {/* Only once the roster has answered (#629): an unread one is not a
          studio with no students to add. */}
      {students.length === 0 && studentsRead ? (
        <div className={s.hint}>{READING_WORDING['event.noStudents']}</div>
      ) : null}
    </>
  );
}

// The category picker.
//
// Keyed on the id, not the label (#128): two categories of one name are two
// options.
//
// An event whose category is not in the list — deleted, or a bare
// `categoryLabel` with no id (#110) — shows the label it carries as a
// placeholder option: `value=""`, `disabled` and `hidden`, so it shows while it
// is the value, is gone from the list once opened, and cannot be chosen back.
// The expense form's Category does the same (#116): `hidden` on an `<option>`
// is unreliable in Safari, and `disabled` alone leaves a greyed row. "Not
// specified" is for an event carrying neither.
function CategoryField({ form, categories, onChange }) {
  const resolved = categories.some((c) => c.id === form.categoryId);
  return (
    <div className={s.spaced}>
      <Field
        label="Category"
        htmlFor="event-category"
        hint="Manage the list from Options → Categories & Locations"
      >
        <select
          id="event-category"
          className={s.input}
          value={resolved ? form.categoryId : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {resolved ? null : (
            <option value="" disabled hidden>{form.categoryLabel || 'Not specified'}</option>
          )}
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
    </div>
  );
}

export default function DetailsSection({
  form, set, students, studentsRead = true, categories, locations, onCategoryChange, shape,
}) {
  // All day is MMS's checkbox beside Duration, so a form with no Duration field
  // has no All day either and nothing on it is disabled — see the Time input.
  const allDay = Boolean(shape.duration && form.allDay);

  return (
    <div className={s.section}>
      <h3 className={s.sectionTitle}>Event Details</h3>

      <Attendees form={form} set={set} students={students} studentsRead={studentsRead} shape={shape} />

      {shape.category && locations.length ? (
        <div className={s.spaced}>
          {/* Only offered once a studio has somewhere to teach. An empty select
              is a promise that the list matters, made before it does. */}
          <Field label="Location" hint="Optional">
            <select
              className={s.input}
              value={form.locationId || ''}
              onChange={(e) => set('locationId', e.target.value)}
            >
              <option value="">Not specified</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </Field>
        </div>
      ) : null}

      {shape.category ? <CategoryField form={form} categories={categories} onChange={onCategoryChange} /> : null}

      <div className={[s.pair, s.spaced].join(' ')}>
        <Field label="Date">
          <DateInput className={s.input}
            value={form.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
        </Field>
        <Field label="Time">
          {/* Disabled, not hidden, and still showing its value, as in MMS
              (#160): ticking All day greys Time out with the time readable,
              and greys Duration out while blanking it. The asymmetry is MMS's;
              unticking gives both fields back as they were.

              `shape.duration` gates the checkbox, so a Quick-Add, which has no
              Duration field, has no All day and this input is never disabled
              there. */}
          <TimeInput className={s.input}
            value={form.startTime}
            disabled={allDay}
            onChange={(e) => set('startTime', e.target.value)}
          />
        </Field>
      </div>

      {shape.duration ? (
        <div className={[s.pair, s.spaced].join(' ')}>
          {/* `step="5"` is advisory: this input is not inside a form that
              submits, so nothing runs constraint validation on it.
              `durationError` says why at the field, and `canSave` in
              hooks/useEventForm.js holds Save shut while it is up (#137); 37
              minutes is a legitimate lesson and 37.5 is not (see
              `durationError` in domain/eventSeries.js).

              `DURATION_MAX_MINUTES` mirrors the rule
              `optionalIntInRange(d, 'durationMin', 1, 1440)` (#148) — a day,
              because a day is all the calendar can draw — so 5,000 minutes is
              refused with a message rather than as a denied save. */}
          <Field
            label="Duration" hint="Minutes"
            // No message while All day is ticked. `canSave` still consults
            // `durationError` over the stored value, since a 37.5 left behind
            // by an untick is still a document Firestore denies, but a red
            // sentence under a blank, greyed box reads as a complaint about a
            // field the teacher cannot reach.
            error={allDay ? null : durationError(form.durationMin, DURATION_MAX_MINUTES)}
          >
            <input
              type="number" min="5" step="5" className={s.input}
              // Blank while All day is ticked, and unticking restores the value,
              // as in MMS. A display rule: `form.durationMin` is untouched
              // underneath.
              value={allDay ? '' : form.durationMin}
              disabled={allDay}
              onChange={(e) => set('durationMin', e.target.value)}
            />
          </Field>

          {/* MMS's All day, in the right-hand column under Time and beside
              Duration (#160). For the document it is a flag, with `startTime`
              and `durationMin` left as they are and not read by anything that
              draws the event (`fieldsFromForm` in hooks/useEventForm.js,
              `allDay` in firestore.rules). */}
          {/* `align-self: end` rather than a second Field with a blank label:
              the pair is a grid, so the checkbox sits on the Duration input's
              baseline instead of level with its caption. */}
          <div className={s.pairEnd}>
            <Check
              label="All day"
              checked={allDay}
              onChange={(e) => set('allDay', e.target.checked)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
